<script setup lang="ts">
import type { ChatMessage } from '~/models/Content/ContentChatbot'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import useStringUtils from '~/utils/stringUtils'
import { stripHtml, stripMarkdown } from '~/utils/dom'
import { PendoTrackName } from '~/models/Pendo'
import usePendo from '~/composables/usePendo'
import Button from '~/components/ui/Button.vue'
import AuDialog from '~/components/ui/AuDialog.vue'

const {
  chatHistory,
  locationId,
} = defineProps<{
  chatHistory: ChatMessage[]
  locationId?: number
}>()

const { t } = useI18n()
const { pendoTrack, pendoFlushNow } = usePendo()
const { truncateToByteLimit } = useStringUtils()

const open = ref(false)
const maxPayloadBytes = 500 // Pendo limit is 512, leave buffer for edge cases

const submitFeedback = () => {
  const encoder = new TextEncoder()

  const cleanMessage = (text: string) => {
    return stripMarkdown(stripHtml(text))
  }

  const formattedChat = chatHistory
    .map((message) => {
      const prefix = message.from === 'assistant' ? 'Bot' : 'Bruker'
      const cleanText = cleanMessage(message.message)
      return `${prefix}: ${cleanText}`
    })
    .join('\n')

  const basePayload = { locationId: locationId ?? 0, chat: '' }
  const basePayloadBytes = encoder.encode(JSON.stringify(basePayload)).length
  const availableChatBytes = maxPayloadBytes - basePayloadBytes

  pendoTrack(PendoTrackName.ChatbotFeedback, {
    locationId: locationId ?? 0,
    chat: truncateToByteLimit(formattedChat, availableChatBytes, true),
  })
  pendoFlushNow()
  open.value = false
}
</script>

<template>
  <AuDialog v-model:open="open" :title="t('chatbot.feedback.title')">
    <template #trigger>
      <button class="w-fit text-(--theme-50) underline underline-offset-2 transition hover:text-(--theme-40) focus-visible:ring">
        {{ t('chatbot.feedback.cta') }}
      </button>
    </template>

    <template #content>
      <div class="space-y-2">
        <p v-text="t('chatbot.feedback.message')" />
        <p v-text="t('chatbot.feedback.disclaimer')" />
        <div class="mt-6 grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            size="lg"
            center
            @click="open = false"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            size="lg"
            center
            @click="submitFeedback"
          >
            {{ t('feedback.send') }}
          </Button>
        </div>
      </div>
    </template>
  </AuDialog>
</template>
